import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Form } from 'antd';
import moment from 'moment';

import { axiosAPI } from '../../../utils/axiosAPI';
import { getAiOptimizerCost } from '../../../utils/helpers/aiOptimizerHelpers';
import geoLocationSelectHelper from '../../../utils/helpers/geoLocationSelectHelper';
import { getArrayOptionsFromObject } from '../../../utils/helpers/getArrayOptionsFromObject';
import { getAxiosHeaders } from '../../../utils/helpers/getAxiosHeaders';
import {
    getKeywordLengthErrorMessage,
    getKeywordUrlErrorMessage,
    isKeywordLengthValid,
    normalizeKeyword,
} from '../../../utils/helpers/keywordHelpers';
import { openBasicErrorNotification } from '../../../utils/helpers/openBasicErrorNotification';
import { isUrlValid, removeUrlExtraSpaces } from '../../../utils/helpers/urlHelpers';
import { useOpen } from '../../../utils/hooks/useOpen';
import { useSetSubscriptionLimits } from '../../../utils/hooks/useSetSubscriptionLimits';
import { useSubscriptionLimits } from '../../../utils/hooks/useSubscriptionLimits';

import { KEY_CODES } from '../../../constants/constants';
import { ContentOptimizerQueueStatus } from '../../../constants/contentOptimizer';
import { countries, defaultCountry, featuredCountries } from '../../../constants/countries';
import { defaultLanguage, featuredLanguages, languages } from '../../../constants/languages';
import { linkSearchParams } from '../../../constants/linkSearchParams';
import { contentBriefPathes } from '../../../constants/queryPathes';
import { statusCodes } from '../../../constants/statusCodes';
import { subscriptionLimits } from '../../../constants/subscriptionLimits';

import { increaseSubscriptionLimits } from '../../pages/account/billing/store/billingSettings.actions';
import {
    setContentOptimizerForUpdate,
    setContentOptimizerMainTotalCount,
} from '../../pages/contentOptimizer/mainPage/store/contentOptimizerMain.actions';
import {
    selectContentOptimizerData,
    selectContentOptimizerMainQueryParams,
    selectIsImportURLActive,
    selectOptimizerForUpdate,
} from '../../pages/contentOptimizer/mainPage/store/contentOptimizerMain.selectors';

import KeywordSearchInput from '../inputs/KeywordSearchInput';
import DebounceSelect from '../selects/DebounceSelect';
import KeywordSelect from '../selects/KeywordSelect';
import AiOptimizerInProgressModal from './AiOptimizerInProgressModal';
import AiOptimizerOptionModal from './AiOptimizerOptionModal';
import DuplicateOptimizerModal from './DuplicateOptimizerModal';
import ExportCheckbox from './ExportCheckbox';
import NoCreditsModal from './NoCreditsModal';
import RunAiOptimizerPopover from './RunAiOptimizerPopover';
import RunOptimizerButton from './RunOptimizerButton';

import {
    selectUserGoogleSearchEnginePreference,
    selectUserLanguagePreference,
} from '../../../userBrowserSettings/store/browserSettings.selectors';

import ContentOptimizerService from '../../../services/contentOptimizer.service';
import { checkRecentIdenticalOptimizerPresence } from '../../../utils/helpers/checkRecentIdenticalOptimizersPresence';

import './CreateNew.scss';

const classes = {
    keywordHeaderWrapper: 'keyword-header-wrapper brief-header d-flex w-100',
    keywordLocationSelect: 'keyword-location-select',
    keywordLanguageSelect: 'keyword-language-select',
    runBriefBtn: 'run-brief-btn',
    changeURLFormItemWrapper: 'change-url-form-item-wrapper',
    breakRow: 'flex-break-row',
};
const defaultAiFieldsData = {
    is_ai_generated: false,
    ai_content_type: '',
    ai_optimizer_options: '',
    ai_additional_info: '',
    ai_include_faqs: false,
};

const CreateSingleSection = ({ getOptimizers, runBtnText, handleCreateSingle }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();

    const { updateSubscriptionLimits } = useSetSubscriptionLimits();
    const { checkIfNotEnoughAiCredits, notEnoughOptimizerCredits } = useSubscriptionLimits();

    const existingOptimizers = useSelector(selectContentOptimizerData);
    const queryParams = useSelector(selectContentOptimizerMainQueryParams);
    const isImportURLActive = useSelector(selectIsImportURLActive);
    const optimizerForUpdate = useSelector(selectOptimizerForUpdate);
    const userLanguagePreference = useSelector(selectUserLanguagePreference) || defaultLanguage;
    const userSearchEnginePreference =
        useSelector(selectUserGoogleSearchEnginePreference) || defaultCountry;

    const [isKeywordInputError, setIsKeywordInputError] = useState(false);
    const [isUrlInputError, setIsUrlInputError] = useState(false);
    const [keywordSearchData, setKeywordSearchData] = useState({
        keywords: '',
        country_code: defaultCountry,
        language_code: userLanguagePreference,
        url: '',
        export_nlp: false,
        ...defaultAiFieldsData,
    });
    const [isResetOptionModal, setIsResetOptionModal] = useState(false);
    const [locationObject, setLocationObject] = useState(
        geoLocationSelectHelper.createLocationObject(userSearchEnginePreference)
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isAiContentPressed, setIsAiContentPressed] = useState(false);

    const { isOpen: isNoCreditModalOpen, handleOpen: handleNoCreditModalOpen } = useOpen();
    const { isOpen: isDuplicateOptimizerModalOpen, handleOpen: handleDuplicateOptimizerModalOpen } =
        useOpen();
    const { isOpen: isAICreditsOpen, handleOpen: handleAICreditsOpen } = useOpen();
    const { isOpen: isOptionMenuOpen, handleOpen: handleOptionMenuOpen } = useOpen(false);
    const { isOpen: isOptionModalOpen, handleOpen: handleOptionModalOpen } = useOpen();
    const { isOpen: isRunningAIOptimizerModalOpen, handleOpen: handleRunningAIOptimizerModalOpen } =
        useOpen();

    const [runningAiOptimizer, setRunningAiOptimizer] = useState(null);

    const setFormFieldErrors = useCallback(
        (fieldName, errorList = []) => {
            form.setFields([
                {
                    name: fieldName,
                    errors: errorList,
                },
            ]);
        },
        [form]
    );

    useEffect(() => {
        if (isImportURLActive && isOptionMenuOpen) {
            handleOptionMenuOpen();
        }
    }, [isImportURLActive, isOptionMenuOpen, handleOptionMenuOpen]);

    useEffect(() => {
        const errorList = keywordSearchData.keywords?.includes(',')
            ? ['You can use only one keyword for this type of brief']
            : [];

        setFormFieldErrors('keywords', errorList);
    }, [keywordSearchData.keywords, setFormFieldErrors]);

    useEffect(() => {
        const serchedKeyword = searchParams?.get(linkSearchParams.keyword.param)?.replace('+', ' ');

        if (serchedKeyword) {
            setKeywordSearchData((prev) => ({
                ...prev,
                keywords: serchedKeyword,
            }));

            form.setFieldsValue({
                keywords: serchedKeyword,
            });
        }

        // eslint-disable-next-line
    }, [searchParams]);

    useEffect(() => {
        if (optimizerForUpdate) {
            setKeywordSearchData((prev) => ({
                ...prev,
                keywords: optimizerForUpdate,
            }));

            form.setFieldsValue({
                keywords: optimizerForUpdate,
            });

            dispatch(setContentOptimizerForUpdate(null));
        }

        if (userSearchEnginePreference) {
            const { country_code, geo_location } = geoLocationSelectHelper.getGeoData(
                userSearchEnginePreference
            );

            if (!country_code && !geo_location) {
                return;
            }

            setKeywordSearchData((prev) => ({
                ...prev,
                country_code,
                geo_location,
            }));
        }

        // eslint-disable-next-line
    }, []);

    const handleChangeKeyword = (event) => {
        const eventValue = normalizeKeyword(event?.target?.value);

        setKeywordSearchData((prev) => ({
            ...prev,
            keywords: eventValue,
        }));

        setIsKeywordInputError(false);
        form.setFields([
            {
                name: 'keywords',
                errors: [],
            },
        ]);
    };

    const handleChangeURL = (event) => {
        const eventValue = removeUrlExtraSpaces(event?.target?.value);

        setKeywordSearchData((prev) => ({
            ...prev,
            url: eventValue,
        }));

        setIsUrlInputError(false);
        form.setFields([
            {
                name: 'url',
                errors: [],
            },
        ]);
    };

    const handleChangeNlpExport = () => {
        setKeywordSearchData((prev) => ({
            ...prev,
            export_nlp: !prev.export_nlp,
        }));
    };

    const handleChangeLocation = (newLocation) => {
        const { country_code, geo_location } = geoLocationSelectHelper.getGeoData(newLocation);

        setKeywordSearchData((prev) => ({
            ...prev,
            country_code,
            geo_location,
        }));

        setLocationObject(geoLocationSelectHelper.createLocationObject(newLocation));
    };

    const handleChangeLanguage = (value) => {
        setKeywordSearchData((prev) => ({
            ...prev,
            language_code: value,
        }));
    };

    const validateRequestData = (isImportURLActive = false) => {
        if (!keywordSearchData.keywords) {
            setIsKeywordInputError(true);

            return false;
        }

        if (!isKeywordLengthValid(keywordSearchData.keywords)) {
            setIsKeywordInputError(true);
            form.setFields([
                {
                    name: 'keywords',
                    errors: [getKeywordLengthErrorMessage()],
                },
            ]);

            return false;
        }

        if (isImportURLActive) {
            if (!keywordSearchData.url) {
                setIsUrlInputError(true);

                return false;
            }

            const isURLValid = isUrlValid(keywordSearchData.url);

            if (!isURLValid) {
                form.setFields([
                    {
                        name: 'url',
                        errors: [getKeywordUrlErrorMessage()],
                    },
                ]);
            }

            return isURLValid;
        }

        return true;
    };

    const isKeywordsFieldEmpty = () => {
        if (!keywordSearchData.keywords) {
            setIsKeywordInputError(true);

            return true;
        }

        return false;
    };

    const notEnoughAiCredits = (isAiGenerated = false, ai_optimizer_options = null) => {
        return isAiGenerated && checkIfNotEnoughAiCredits(ai_optimizer_options);
    };

    const createContentBrief = (aiFormData = {}) => {
        const isFormValid = validateRequestData(isImportURLActive);

        if (isFormValid) {
            if (isOptionMenuOpen) {
                handleOptionMenuOpen();
            }
            setIsLoading(true);

            const { export_nlp, ...dataToSend } = keywordSearchData;
            const aiDataToSend = aiFormData?.is_ai_generated
                ? {
                      ...aiFormData,
                  }
                : {
                      // Reset the AI data we send if no aiFormData = it is not AI optimizer
                      ...defaultAiFieldsData,
                  };

            axiosAPI
                .post(
                    contentBriefPathes.createContentBrief,
                    {
                        ...dataToSend,
                        ...aiDataToSend,
                        user_created_at_time: new Date().toISOString(),
                    },
                    {
                        ...getAxiosHeaders(),
                        params: {
                            get_optimizers: getOptimizers,
                            export_nlp: export_nlp,
                            per_page: queryParams?.per_page,
                        },
                    }
                )
                .then((result) => {
                    if (result?.status === statusCodes.create) {
                        handleCreateSingle(
                            result?.data.map((item) => ({ ...item, localClientTime: moment() }))
                        );
                        dispatch(
                            increaseSubscriptionLimits({
                                limit: subscriptionLimits.optimizersUsed,
                                value: 1,
                            })
                        );
                        if (aiFormData?.is_ai_generated) {
                            dispatch(
                                increaseSubscriptionLimits({
                                    limit: subscriptionLimits.openaiBriefsUsed,
                                    value: getAiOptimizerCost(aiFormData?.ai_optimizer_options),
                                })
                            );
                        }
                        dispatch(
                            setContentOptimizerMainTotalCount(
                                result?.headers?.['x-total-pages'] * queryParams?.per_page
                            )
                        );
                        setKeywordSearchData((prev) => ({
                            ...prev,
                            keywords: '',
                            url: '',
                            export_nlp: false,
                            ...defaultAiFieldsData,
                        }));

                        form.setFieldValue('keywords', '');
                        form.setFieldValue('url', '');
                        form.setFieldValue('is_ai_generated', false);
                        form.setFieldValue('export_nlp', false);
                    }
                })
                .catch(({ response }) => {
                    updateSubscriptionLimits();
                    openBasicErrorNotification(response?.data?.message);
                })
                .finally(() => {
                    setIsLoading(false);
                    setIsResetOptionModal(true);
                });
        }
    };

    const isOptimizerDuplicateFound = () => {
        const isRecentIdenticalOptimizerPresent = checkRecentIdenticalOptimizerPresence(
            existingOptimizers,
            keywordSearchData,
            keywordSearchData.geo_location
                ? `${keywordSearchData.country_code}_${keywordSearchData.geo_location}`
                : keywordSearchData.country_code
        );

        if (isRecentIdenticalOptimizerPresent) {
            handleDuplicateOptimizerModalOpen();
        }

        return isRecentIdenticalOptimizerPresent;
    };

    const handleRunBrief = (aiFormData = {}) => {
        if (isLoading) {
            return;
        }

        if (notEnoughOptimizerCredits) {
            handleNoCreditModalOpen();
            handleOptionMenuOpen(false);

            return;
        }

        if (notEnoughAiCredits(aiFormData?.is_ai_generated, aiFormData?.ai_optimizer_options)) {
            handleAICreditsOpen();
            handleOptionMenuOpen(false);

            return;
        }

        createContentBrief(aiFormData);
    };

    const handleKeyDown = (event) => {
        if (event.key === KEY_CODES.ENTER) {
            if (isImportURLActive) {
                handleRunBrief();
            } else if (!isKeywordsFieldEmpty()) {
                handleOptionMenuOpen();
            }
        }
    };

    const handleCancelRecentIdenticalOptimizer = () => {
        form.setFieldValue('is_ai_generated', false);
        setKeywordSearchData((prev) => ({
            ...prev,
            export_nlp: false,
            ...defaultAiFieldsData,
        }));
        handleDuplicateOptimizerModalOpen();
        if (isOptionMenuOpen) handleOptionMenuOpen();
    };

    const handleRunRecentIdenticalOptimizer = () => {
        if (isAiContentPressed) {
            handleOptionModalOpen();
        } else {
            handleRunBrief();
        }
    };

    /** AI Optimizer actions - Start */
    const handleSubmitAiForm = (aiFormData = {}) => {
        handleOptionModalOpen(false);
        setKeywordSearchData((prev) => ({
            ...prev,
            ...aiFormData,
        }));
        handleRunBrief(aiFormData);
    };

    const checkKeywordsAndOpenOptionModal = (_) => {
        const existingRunningAIOptimizer = existingOptimizers.find(
            (optimizer) =>
                optimizer.content_brief.is_ai_generated &&
                [
                    ContentOptimizerQueueStatus.QUEUED,
                    ContentOptimizerQueueStatus.IN_PROGRESS,
                    ContentOptimizerQueueStatus.NOT_STARTED,
                ].includes(optimizer.queue_status)
        );

        if (existingRunningAIOptimizer) {
            handleRunningAIOptimizerModalOpen();
            setRunningAiOptimizer(existingRunningAIOptimizer);
            return;
        }

        setIsAiContentPressed(true);

        if (!isKeywordsFieldEmpty() && !isOptimizerDuplicateFound()) {
            handleOptionModalOpen();
        }
    };

    const closeAiOptimizerInProgressModal = () => {
        setRunningAiOptimizer(null);
        handleRunningAIOptimizerModalOpen();
    };
    /** AI Optimizer actions - End */

    const checkKeywordsAndRunSimpleBrief = () => {
        setIsAiContentPressed(false);

        if (!isKeywordsFieldEmpty() && !isOptimizerDuplicateFound()) {
            handleRunBrief();
        }
    };

    const getRunOptimizerButton = (showSimpleButton = true) => {
        const buttonComponent = (
            <RunOptimizerButton
                isLoading={isLoading}
                runBtnText={runBtnText}
                handleRunBrief={showSimpleButton ? () => handleRunBrief() : handleOptionMenuOpen}
            />
        );

        if (showSimpleButton) {
            return buttonComponent;
        }

        return (
            <RunAiOptimizerPopover
                isOpen={isOptionMenuOpen}
                handleRunBrief={checkKeywordsAndRunSimpleBrief}
                handleRunAIBrief={checkKeywordsAndOpenOptionModal}
                onClose={() => {
                    handleOptionMenuOpen(false);
                }}
            >
                {buttonComponent}
            </RunAiOptimizerPopover>
        );
    };

    return (
        <>
            <Form form={form} onKeyDown={handleKeyDown}>
                <div className={classes.keywordHeaderWrapper}>
                    <Form.Item name='keywords'>
                        <KeywordSearchInput
                            placeholder='Enter keyword or phrase you want to rank for (e.g. best running shoes)...'
                            value={keywordSearchData.keywords}
                            onChange={handleChangeKeyword}
                            isInputError={isKeywordInputError}
                        />
                    </Form.Item>
                    <div className={classes.keywordLocationSelect}>
                        <DebounceSelect
                            placeholder='Enter city, state/province, or country'
                            onChange={handleChangeLocation}
                            showSearch={true}
                            value={locationObject}
                            setValue={() => {}}
                            defaultValue={locationObject}
                            fetchOptions={(value) => ContentOptimizerService.getGeoTargets(value)}
                            initialOptions={getArrayOptionsFromObject({
                                values: countries,
                                featured: featuredCountries,
                            })}
                        />
                    </div>
                    <div className={classes.keywordLanguageSelect}>
                        <KeywordSelect
                            placeholder='Select Language'
                            options={getArrayOptionsFromObject({
                                values: languages,
                                featured: featuredLanguages,
                            })}
                            defaultValue={keywordSearchData.language_code}
                            onChange={handleChangeLanguage}
                        />
                    </div>
                    <Form.Item shouldUpdate>
                        {() => getRunOptimizerButton(isImportURLActive)}
                    </Form.Item>

                    {isImportURLActive && (
                        <>
                            <div className={classes.breakRow} />
                            <Form.Item name='url' className={classes.changeURLFormItemWrapper}>
                                <KeywordSearchInput
                                    isInputError={isUrlInputError}
                                    placeholder='Enter URL to import existing content (e.g. https://www.domain.com/best-running-shoes)...'
                                    value={keywordSearchData.url}
                                    onChange={handleChangeURL}
                                />
                            </Form.Item>
                        </>
                    )}
                    <ExportCheckbox
                        value={keywordSearchData.export_nlp}
                        onChange={handleChangeNlpExport}
                    />
                </div>
            </Form>
            <DuplicateOptimizerModal
                isOpen={isDuplicateOptimizerModalOpen}
                optimizerData={{
                    keyword: keywordSearchData.keywords,
                    countryCode: keywordSearchData.country_code,
                    languageCode: keywordSearchData.language_code,
                }}
                locationObject={locationObject}
                handleOpen={handleDuplicateOptimizerModalOpen}
                handleRunOptimizer={handleRunRecentIdenticalOptimizer}
                handleCancel={handleCancelRecentIdenticalOptimizer}
            />
            <NoCreditsModal isOpen={isNoCreditModalOpen} handleOpen={handleNoCreditModalOpen} />
            <NoCreditsModal
                isOpen={isAICreditsOpen}
                handleOpen={handleAICreditsOpen}
                title='Not enough AI credits'
                isAiType
            />
            <AiOptimizerOptionModal
                isOpen={isOptionModalOpen}
                handleOpen={handleOptionModalOpen}
                onSubmit={handleSubmitAiForm}
                resetFormData={isResetOptionModal}
                keywordData={{
                    language_code: keywordSearchData.language_code,
                    country_code: keywordSearchData.country_code,
                    keyword: keywordSearchData.keywords,
                    location: locationObject,
                }}
                showOptions
            />
            {runningAiOptimizer && (
                <AiOptimizerInProgressModal
                    isOpen={isRunningAIOptimizerModalOpen}
                    optimizerData={{
                        keyword: runningAiOptimizer.content_brief?.keywords,
                        countryCode: runningAiOptimizer.content_brief?.country_code,
                        languageCode: runningAiOptimizer.content_brief?.language_code,
                    }}
                    locationObject={locationObject}
                    handleClose={closeAiOptimizerInProgressModal}
                />
            )}
        </>
    );
};

export default CreateSingleSection;
